import { Component } from "@angular/core";
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import _ from "lodash";
import { AgFns, GridOptionsExt } from "@/shared/ag-fns";
import { DialogBaseComponent, DialogParams } from "@/shared/dialog-base.component";
import { DomainService } from "@/shared/domain.service";
import { ActiveStatusEnum, FeatureChoice } from "@/model/entities/entity-model";
import { FeedSizeFns } from "@/shared/feed-size-fns";
import { StagedFeedApiService } from "@/admin/staged-feed-api.service";
import { SizeMappingCandidateDTO } from "@/admin/staged-feed.models";

export interface SizeMappingDialogParams extends DialogParams {
  manufacturerId: number;
  manufacturerName: string;

  /**
   * The questions the caller actually came to answer, so the screen opens on them.
   *
   * Optional, and narrowing only. Two depths, depending on what the caller knows.
   *
   * A style's health panel knows which sizes are outstanding AND which list they belong to, so it
   * passes both and the view narrows to those rows; making somebody find them again among a
   * hundred is work the screen can do for them. The Manufacturer Feed page knows only which scale
   * the selected style is built on, so it passes the list alone - still worth narrowing by,
   * because the queue runs all eleven scales together and a trouser style has no business
   * opening on alpha-size questions.
   *
   * The answer still travels - it is recorded against the size list, not the style - which is why
   * the focus can be dropped without reloading.
   */
  focusFeatureId?: number;
  focusFeedSizes?: string[];
  focusLabel?: string;

  /** The size list's name, for the banner. Only read when focusFeedSizes is empty. */
  focusFeatureName?: string;

  /**
   * Sizes to offer although they are ALREADY answered, on focusFeatureId.
   *
   * The queue is built from sizes with no answer, so an answer that is wrong but valid - feed 2XL
   * pointed at our 3X - is unreachable from here: it is answered, it builds products, and nothing
   * downstream complains. Only the style's health panel notices, because only it compares the
   * mapping against the name, so the panel names the size and this screen offers it back.
   *
   * Named sizes only. A blanket 'show the answered ones' would turn a short work queue into a
   * list of everything, which is the one thing that makes it stop being worked.
   */
  reopenFeedSizes?: string[];
}

/** What one Uniformax size list proves about how it writes sizes. */
interface ListFormat {
  /** Shapes the list already writes exactly as the feed does, such as '#-#'. */
  proven: Set<string>;
  /** Every value on the list, any status, lower-cased. The list allows each value once. */
  taken: Set<string>;
}

interface SizeChoice {
  id: number;
  label: string;
  /** FeatureChoice.displayOrder - sizes have a natural order the alphabet does not preserve. */
  order: number;
}

/**
 * Says what each size the manufacturer publishes means in Uniformax.
 *
 * Color alignment worked by renaming our data to match the feed, because Proximity's image lookup
 * joins on the color name. Nothing joins on a size string, and Uniformax's eleven size schemes
 * exist for real reasons - trousers by waist and inseam, shoes, scrubs - so size is answered by
 * mapping instead. One answer here covers every style using that size feature.
 *
 * The screen proposes an answer wherever it can. On SanMar that covers 62 of 155 questions but
 * 81.5% of the parts, because the rules land on exactly the sizes that carry the volume: 2XL,
 * 3XL, OSFA, and the four-digit trouser sizes. Confirming a proposal is a click; the rest is where
 * a person's judgement is actually worth something.
 *
 * NOTHING HERE TOUCHES A PRODUCT. The answers are recorded against the staged feed rows only.
 */
@Component({
  templateUrl: "./size-mapping-dialog.component.html",
  standalone: false,
})
export class SizeMappingDialogComponent extends DialogBaseComponent<SizeMappingDialogParams> {
  gridOptions: GridOptionsExt;

  candidates: SizeMappingCandidateDTO[] = [];
  selected: SizeMappingCandidateDTO | null = null;

  /** Choices of the selected question's own feature. '2X' exists in several; only one is right. */
  choices: SizeChoice[] = [];
  chosenId: number | null = null;

  isLoading = false;
  isApplying = false;
  errorMessage = "";
  answeredCount = 0;
  answeredParts = 0;

  /** Showing only the caller's questions. False once the operator asks for the whole queue. */
  isFocused = false;

  /**
   * Sizes being offered back although they are already answered.
   *
   * Held here rather than read off params because it shrinks: a reopened size that gets its new
   * answer stops being reopened, so the row leaves on the next read exactly as an ordinary
   * answered question does. Left in, it would sit there answered and correct, reading as work
   * still outstanding.
   */
  private reopened: string[] = [];

  /**
   * Feed sizes on screen that can go onto their Uniformax size list exactly as the feed writes them.
   *
   * Only where the list already writes sizes of that shape the feed's way - 52-26 onto a list that
   * holds 30-32 from the same feed. A feed '28 36U' against our '28U' has no such proof, so it
   * stays a question for a person to match.
   */
  addable: SizeMappingCandidateDTO[] = [];

  /** Per size list. Dropped for a list as soon as sizes are added to it. */
  private listFormats = new Map<number, ListFormat>();

  constructor(
    public config: DynamicDialogConfig<SizeMappingDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private api: StagedFeedApiService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: "What the Feed Sizes Mean", width: "1100px", height: "860px" });

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,
      // Click as well as selection. AG Grid raises nothing when an already-selected row is
      // clicked, and the queue reloads after every answer - with a stable getRowId the grid
      // restores the highlight, so the row looks picked while the panel below has nothing.
      // Filtering to one row made it obvious: the only row was highlighted and inert.
      onRowClicked: this.onRowClicked,
      rowSelection: "single",
      cellSelection: false,
      getRowId: (p: GetRowIdParams) => SizeMappingDialogComponent.rowKey(p.data as SizeMappingCandidateDTO),
      getRowClass: (p: any) =>
        ((p.data as SizeMappingCandidateDTO)?.ProposedFeatureChoiceId == null ? "rs-already-processed" : ""),
    });

    this.prepare();
  }

  static rowKey(c: SizeMappingCandidateDTO) {
    return `${c?.FeatureId}|${c?.FeedSize}|${c?.ApparelStyle ?? ""}`;
  }

  private async prepare() {
    this.reopened = (this.params.reopenFeedSizes ?? []).filter(s => (s ?? "").trim().length > 0);
    this.isFocused = this.hasFocus;
    await this.load();
    this.isPageReady = true;
  }

  private async load() {
    this.isLoading = true;
    try {
      // The feature is required for a reopen - 'L' means different things on different lists, and
      // reopening every list's L would be the opposite of what was asked for.
      const reopen = this.params.focusFeatureId != null && this.reopened.length > 0
        ? { featureId: this.params.focusFeatureId, feedSizes: this.reopened }
        : undefined;
      this.candidates = await this.api.getSizeMappingCandidates(this.params.manufacturerId, reopen);

      // The focus stays when the caller's questions run out. Dropping it used to put the
      // manufacturer's whole queue on screen the moment a style was finished - hundreds of sizes on
      // other lists, with Accept All and Add buttons that had nothing to do with the style the
      // operator came from. A finished focus says it is finished instead; see isFinished.

      // May be a no-op: on first load the grid does not exist yet, because the template is gated
      // on isPageReady. onGridReady pushes the rows instead. Both orders have to work - the data
      // can arrive before the grid or after it, depending on whether this is the initial open or
      // a reload after an answer.
      this.gridOptions.apiX?.setGridOption("rowData", this.visibleCandidates);
      // The answered row is gone but its highlight can survive the reload, which reads as a
      // selection that does nothing. Clear it so the next click is unambiguous.
      this.gridOptions.apiX?.deselectAll();
      await this.refreshAddable();
    } finally {
      this.isLoading = false;
    }
  }

  private async refreshAddable() {
    const open = this.visibleCandidates.filter(c => c.ProposedFeatureChoiceId == null && !c.CurrentChoiceValue);
    for (const featureId of _.uniq(open.map(c => c.FeatureId))) {
      if (!this.listFormats.has(featureId)) this.listFormats.set(featureId, await this.readListFormat(featureId));
    }
    this.addable = open.filter(c => {
      const f = this.listFormats.get(c.FeatureId);
      return f != null
        && !f.taken.has((c.FeedSize ?? "").trim().toLowerCase())
        && f.proven.has(FeedSizeFns.shape(c.FeedSize));
    });
  }

  private async readListFormat(featureId: number): Promise<ListFormat> {
    const [exact, choices] = await Promise.all([
      this.api.getExactSizeMatches(this.params.manufacturerId, featureId),
      this.dbQueryService.getFeatureChoices(featureId),
    ]);
    return {
      proven: FeedSizeFns.provenShapes(exact),
      taken: new Set(choices.map(fc => (fc.choiceValue ?? "").trim().toLowerCase())),
    };
  }

  /** One per size list and value. The same feed size can appear once per cut. */
  get addableSizes(): { featureId: number; featureName: string; value: string }[] {
    return _.uniqBy(
      this.addable.map(c => ({ featureId: c.FeatureId, featureName: c.FeatureName, value: c.FeedSize.trim() })),
      x => `${x.featureId}|${x.value.toLowerCase()}`
    );
  }

  /**
   * Add the feed sizes the Uniformax size list has no name for, then match each one to its new size.
   *
   * Saved through Breeze on this dialog's manager, which is the page's. Anything the page has not
   * saved would go out in the same save, so the dialog refuses until the page is clean.
   */
  async onAddToSizeList() {
    const sizes = this.addableSizes;
    if (sizes.length === 0 || this.isApplying) return;

    if (this.dbSaveService.hasChanges()) {
      await this.dialogService.showOkMessage(
        "Save the Page First",
        "The page behind this dialog has changes that are not saved. Close this dialog."
          + " Save or undo the changes on the page. Then try again."
      );
      return;
    }

    const byList = _.groupBy(sizes, s => s.featureId);
    const blocks = Object.values(byList).map(list => {
      const values = list.map(s => s.value)
        .sort((a, b) => FeedSizeFns.compareNumbers(FeedSizeFns.numbers(a), FeedSizeFns.numbers(b)));
      return `The system adds ${values.length} size(s) to the Uniformax size list '${list[0].featureName}':`
        + `<br/><br/>${values.join(", ")}`;
    });

    const ok = await this.dialogService.yesNoDialog(
      "Add Feed Sizes to the Uniformax Size List",
      blocks.join("<br/><br/>")
        + `<br/><br/>Each size is written exactly as the feed writes it.`
        + ` The list already writes sizes of this shape the same way as the feed.`
        + `<br/><br/>Then the system matches each feed size to its new Uniformax size.`
        + ` It does not change any product.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isApplying = true;
    this.errorMessage = "";
    try {
      const created = new Map<string, FeatureChoice>();
      for (const [featureIdText, list] of Object.entries(byList)) {
        const featureId = Number(featureIdText);
        const choices = await this.dbQueryService.getFeatureChoices(featureId);
        const plan = FeedSizeFns.placeNewSizes(
          choices.map(fc => ({ id: fc.id, value: fc.choiceValue, order: fc.displayOrder ?? 0 })),
          list.map(s => s.value)
        );
        plan.reorders.forEach(r => {
          const fc = choices.find(c => c.id === r.id);
          if (fc) fc.displayOrder = r.order;
        });
        list.forEach(s => {
          const fc = this.dbSaveService.createEntity(FeatureChoice, {
            featureId,
            choiceValue: s.value,
            displayOrder: plan.orders.get(s.value),
            activeStatusId: ActiveStatusEnum.Active,
          });
          created.set(`${featureId}|${s.value.toLowerCase()}`, fc);
        });
      }

      const saved = await this.dbSaveService.saveChanges();
      if (!saved) return;
      Object.keys(byList).forEach(id => this.listFormats.delete(Number(id)));

      // One call per row, cut included, because that is the grain an answer is recorded at.
      let sizesDone = 0;
      let partsDone = 0;
      for (const q of this.addable) {
        const fc = created.get(`${q.FeatureId}|${q.FeedSize.trim().toLowerCase()}`);
        if (fc == null) continue;
        try {
          const r = await this.api.applySizeMapping({
            manufacturerId: this.params.manufacturerId,
            feedSize: q.FeedSize,
            apparelStyle: q.ApparelStyle,
            featureId: q.FeatureId,
            featureChoiceId: fc.id,
          });
          sizesDone++;
          partsDone += r.PartsUpdated;
        } catch (e: any) {
          this.errorMessage =
            `The system added the sizes. It matched ${sizesDone} feed size(s) and then stopped at the feed size '${q.FeedSize}'.`
            + ` ${e?.error?.Message ?? e?.message ?? ""}`.trimEnd()
            + ` Select the feed size to match it.`;
          break;
        }
      }

      this.answeredCount += sizesDone;
      this.answeredParts += partsDone;
      this.toastr.success(
        `The system added ${sizes.length} size(s) to the Uniformax size list and matched ${partsDone} feed part(s).`);

      this.selected = null;
      this.choices = [];
      this.chosenId = null;
      await this.load();
    } finally {
      this.isApplying = false;
    }
  }

  /**
   * A size list alone is enough to narrow on.
   *
   * Two callers, two depths. A style's health panel knows the exact sizes it is waiting on and
   * passes both. The feed viewer knows only which scale the selected style is built on - and
   * that alone is worth narrowing by, because the manufacturer's queue runs all eleven scales
   * together, so selecting a trouser style and pressing Match Sizes opened on a hundred
   * alpha-size questions belonging to other styles.
   */
  get hasFocus() {
    return this.params.focusFeatureId != null;
  }

  /** The caller's own questions: the size list always, the named sizes when it gave any. */
  get focusedCandidates(): SizeMappingCandidateDTO[] {
    if (!this.hasFocus) return [];
    const onFeature = this.candidates.filter(c => c.FeatureId === this.params.focusFeatureId);
    if ((this.params.focusFeedSizes?.length ?? 0) === 0) return onFeature;

    const wanted = new Set((this.params.focusFeedSizes ?? []).map(s => (s ?? "").trim().toLowerCase()));
    return onFeature.filter(c => wanted.has((c.FeedSize ?? "").trim().toLowerCase()));
  }

  get visibleCandidates(): SizeMappingCandidateDTO[] {
    return this.isFocused ? this.focusedCandidates : this.candidates;
  }

  /** The caller's questions are all answered. The dialog offers nothing more. */
  get isFinished() {
    return this.isFocused && this.focusedCandidates.length === 0;
  }

  /**
   * Whether the whole queue may be shown at all.
   *
   * Not when the caller named the sizes - a product type's health panel. That context is one style,
   * and the rest of the manufacturer's queue is somebody else's work. The Manufacturer Feed page
   * narrows by size list only, and working across styles is what that page is for.
   */
  get canShowEverything() {
    return !this.params.focusFeedSizes?.length;
  }

  /** At least one size on screen is here to have its answer changed rather than given. */
  get isReopening() {
    return this.reopened.length > 0;
  }

  /** Drop the focus and show the manufacturer's whole queue. No reload - the rows are already here. */
  async showEverything() {
    this.isFocused = false;
    this.gridOptions.apiX?.setGridOption("rowData", this.visibleCandidates);
    this.gridOptions.apiX?.deselectAll();
    this.selected = null;
    await this.refreshAddable();
  }

  /**
   * A reopened size that now has its new answer stops being reopened.
   *
   * Without this the row comes straight back on the reload, answered and correct, and reads as
   * work still outstanding - and Accept All would offer to answer it again.
   */
  private markAnswered(feedSize: string) {
    const key = (feedSize ?? "").trim().toLowerCase();
    this.reopened = this.reopened.filter(s => (s ?? "").trim().toLowerCase() !== key);
  }

  get remainingQuestions() {
    return this.candidates.length;
  }

  /**
   * The summary card counts what the grid shows. Opened from a product type it used to count the
   * manufacturer's whole queue - 741 sizes above a grid of 5 - which is the same loss of context
   * as showing that queue. remainingQuestions stays whole, because Show all is about the whole.
   */
  get shownQuestions() {
    return this.visibleCandidates.length;
  }

  get shownParts() {
    return _.sumBy(this.visibleCandidates, c => c.PartCount);
  }

  get shownWithProposal() {
    return this.visibleCandidates.filter(c => c.ProposedFeatureChoiceId != null).length;
  }

  /**
   * The suggestions on screen, which is what Accept All acts on.
   *
   * Scoped to what is VISIBLE rather than to the whole queue. Opened from a style's health panel
   * the grid is narrowed to that style's outstanding sizes, and a button that quietly answered a
   * hundred others as well would be a very unpleasant surprise.
   */
  get visibleWithProposal(): SizeMappingCandidateDTO[] {
    return this.visibleCandidates.filter(c => c.ProposedFeatureChoiceId != null);
  }

  get canAcceptAll() {
    return !this.isApplying && this.visibleWithProposal.length > 0;
  }

  /**
   * Record every suggestion on screen in one go.
   *
   * The suggestions are the ones the system can already justify - an exact name match, or a rule
   * it names in the Why column - so confirming them one at a time is a hundred presses of a
   * button to agree with something that was already worked out. The ones with no suggestion are
   * untouched; those are where a person's judgement is actually worth something.
   *
   * One call per size rather than a bulk endpoint, because applySizeMapping already exists and
   * is proven. Each is independent, so a failure part way leaves the earlier answers recorded
   * and the rest still in the queue - pressing again picks up where it stopped. The count says
   * what actually happened rather than what was attempted.
   */
  async onAcceptAllSuggested() {
    const batch = this.visibleWithProposal;
    if (batch.length === 0 || this.isApplying) return;

    const parts = _.sumBy(batch, c => c.PartCount);
    const ok = await this.dialogService.yesNoDialog(
      "Record Every Suggested Size",
      `The system records the suggested Uniformax size for ${batch.length} feed size(s) from`
        + ` ${this.params.manufacturerName}.`
        + `<br/><br/>This affects ${parts} feed part(s).`
        + ` It does not change any product.`
        + `<br/><br/>Feed sizes with no suggestion are left for you to answer.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isApplying = true;
    this.errorMessage = "";
    let sizesDone = 0;
    let partsDone = 0;
    try {
      for (const q of batch) {
        try {
          const r = await this.api.applySizeMapping({
            manufacturerId: this.params.manufacturerId,
            feedSize: q.FeedSize,
            apparelStyle: q.ApparelStyle,
            featureId: q.FeatureId,
            featureChoiceId: q.ProposedFeatureChoiceId,
          });
          sizesDone++;
          partsDone += r.PartsUpdated;
          this.markAnswered(q.FeedSize);
        } catch (e: any) {
          // Name the size that stopped it. 'The system could not record the size' after sixty
          // successes says nothing about which one to go and look at.
          this.errorMessage =
            `The system recorded ${sizesDone} feed size(s) and then stopped at the feed size '${q.FeedSize}'.`
            + ` ${e?.error?.Message ?? e?.message ?? ""}`.trimEnd()
            + ` Press the button again to continue from there.`;
          break;
        }
      }

      this.answeredCount += sizesDone;
      this.answeredParts += partsDone;
      if (sizesDone > 0) {
        this.toastr.success(`The system matched ${sizesDone} feed size(s) and ${partsDone} feed part(s).`);
      }

      this.selected = null;
      this.choices = [];
      this.chosenId = null;
      await this.load();
    } finally {
      this.isApplying = false;
    }
  }

  get canApply() {
    return this.selected != null && this.chosenId != null && !this.isApplying;
  }

  async onRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) return;
    await this.selectRow(e.data as SizeMappingCandidateDTO);
  }

  onRowClicked = async (e: any) => {
    await this.selectRow(e.data as SizeMappingCandidateDTO);
  };

  private async selectRow(row: SizeMappingCandidateDTO) {
    if (row == null) return;
    this.selected = row;
    this.errorMessage = "";
    await this.loadChoicesForSelected();
  }

  /**
   * Only the selected question's own feature. Offering all eleven size lists would invite an
   * answer from the wrong family, which is the one mistake this screen must not make easy.
   */
  private async loadChoicesForSelected() {
    // Hold the row this call is for. Re-reading this.selected after the await is what broke:
    // clicking a second row, or answering one, moves the selection on while the fetch is in
    // flight - and an answer sets it to null, so the continuation read a property off nothing.
    const row = this.selected;

    this.choices = [];
    this.chosenId = null;
    if (row == null) return;

    const all = await this.dbQueryService.getFeatureChoices(row.FeatureId);

    // Someone has moved on. Dropping this result is right twice over: it cannot null-reference,
    // and a slow first fetch can no longer land on top of a faster second one.
    if (this.selected !== row) return;

    // displayOrder, not alphabetical. Sizes have a natural order and the alphabet destroys it -
    // 'M Long, M Regular, M Short, S Long' reads as nonsense to anyone looking for a size.
    this.choices = _.sortBy(
      all.filter(fc => fc.activeStatusId !== ActiveStatusEnum.Inactive)
         .map(fc => ({ id: fc.id, label: fc.choiceValue, order: fc.displayOrder ?? 0 })),
      [c => c.order, c => c.label.toLowerCase()]
    );

    // Pre-select the proposal so the common case is one button press.
    this.chosenId = row.ProposedFeatureChoiceId;
  }

  async onApply() {
    if (!this.canApply || this.selected == null || this.chosenId == null) return;
    const q = this.selected;
    const label = this.choices.find(c => c.id === this.chosenId)?.label ?? "";

    // Say what it replaces. The operator came here to correct an answer, and a message that only
    // names the new one does not let them check they are correcting the one they meant.
    const replaces = q.CurrentChoiceValue
      ? `<br/><br/>This feed size means '${q.CurrentChoiceValue}' today. The system replaces that answer.`
      : "";

    const ok = await this.dialogService.yesNoDialog(
      "Record the Size",
      `The system records that the feed size '${q.FeedSize}' means '${label}' in Uniformax,`
        + ` for every style that uses the ${q.FeatureName} Uniformax size list.`
        + replaces
        + `<br/><br/>This affects ${q.PartCount} feed part(s) on ${q.StyleCount} style(s).`
        + ` It does not change any product.`
        + `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isApplying = true;
    this.errorMessage = "";
    try {
      const r = await this.api.applySizeMapping({
        manufacturerId: this.params.manufacturerId,
        feedSize: q.FeedSize,
        apparelStyle: q.ApparelStyle,
        featureId: q.FeatureId,
        featureChoiceId: this.chosenId,
      });
      this.answeredCount++;
      this.answeredParts += r.PartsUpdated;
      this.markAnswered(q.FeedSize);
      this.toastr.success(
        `The system matched ${r.PartsUpdated} feed part(s) on ${r.StylesAffected} style(s) to '${r.ChoiceValue}'.`);
      this.selected = null;
      this.choices = [];
      this.chosenId = null;
      await this.load();
    } catch (e: any) {
      this.errorMessage = e?.error?.Message ?? e?.message ?? "The system could not record the feed size.";
    } finally {
      this.isApplying = false;
    }
  }

  onGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      { headerName: "Feed Size", field: "FeedSize", filter: "agTextColumnFilter", ...AgFns.flexWidth(160) },
      { headerName: "Cut", field: "ApparelStyle", filter: "agSetColumnFilter", ...AgFns.flexWidth(120) },
      { headerName: "Uniformax Size List", field: "FeatureName", filter: "agSetColumnFilter", ...AgFns.flexWidth(210) },
      // Only a reopened row carries an answer already, so the column is hidden for the ordinary
      // queue - every cell would read '—' and a column of dashes teaches nobody anything.
      {
        headerName: "Uniformax Size Now",
        field: "CurrentChoiceValue",
        hide: this.reopened.length === 0,
        ...AgFns.flexWidth(170),
        valueFormatter: (p: any) => p.value ?? "—",
      },
      {
        headerName: "Suggested Uniformax Size",
        field: "ProposedChoiceValue",
        ...AgFns.flexWidth(190),
        valueFormatter: (p: any) => p.value ?? "—",
      },
      {
        headerName: "Why",
        field: "ProposedRule",
        ...AgFns.flexWidth(160),
        valueFormatter: (p: any) => p.value ?? "needs a person",
      },
      { headerName: "Parts", field: "PartCount", type: "numericColumn", ...AgFns.fixWidth(100) },
      { headerName: "Styles", field: "StyleCount", type: "numericColumn", ...AgFns.fixWidth(100) },
    ];
    // Biggest first: the volume sits in a handful of questions, so the work front-loads.
    AgFns.initGrid(event, colDefs, [{ colId: "PartCount", sort: "desc" }]);
    // The fetch finishes before the grid exists, so the rows are pushed here rather than there.
    this.gridOptions.apiX?.setGridOption("rowData", this.visibleCandidates);
  };

  onClose() {
    this.dialogRef.close(this.answeredCount > 0 ? { answered: this.answeredCount } : null);
  }
}
